import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO, isToday, isTomorrow, isYesterday } from 'date-fns';
import { ka } from 'date-fns/locale';
import TicketCard from '../components/TicketCard';
import firebaseService from '../services/firebaseService';
import './HighRisk.css';

const HighRisk = () => {
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [updatingCards, setUpdatingCards] = useState(new Set());

  // Group tickets by date
  const groupTicketsByDate = (tickets) => {
    const groups = {};

    tickets.forEach(ticket => {
      const dateKey = ticket.date || 'უცნობი თარიღი';
      if (!groups[dateKey]) {
        groups[dateKey] = [];
      }
      groups[dateKey].push(ticket);
    });

    return groups;
  };

  // Format date for display in Georgian
  const formatDateHeader = (dateStr) => {
    try {
      const date = parseISO(dateStr);

      if (isToday(date)) {
        return '🎯 დღეს - ' + format(date, 'dd MMMM yyyy', { locale: ka });
      }
      if (isTomorrow(date)) {
        return '📅 ხვალ - ' + format(date, 'dd MMMM yyyy', { locale: ka });
      }
      if (isYesterday(date)) {
        return '🕐 გუშინ - ' + format(date, 'dd MMMM yyyy', { locale: ka });
      }

      return '📆 ' + format(date, 'dd MMMM yyyy', { locale: ka });
    } catch {
      return dateStr;
    }
  };

  useEffect(() => {
    let unsubscribe = null;
    let isMounted = true;

    const setupSubscription = async () => {
      try {
        // Set up real-time listener for automatic updates
        unsubscribe = await firebaseService.subscribeToMatches((updatedMatches) => {
          if (!isMounted) return;

          console.log('HighRisk - Received matches:', updatedMatches);

          // Filter: Show only high-risk and forecast category matches (all dates, not just today)
          let highRiskMatches = updatedMatches.filter(match =>
            match.category === 'high-risk' || match.category === 'forecast'
          );

          console.log('HighRisk - Filtered matches (high-risk category only):', highRiskMatches);

          setTickets(highRiskMatches);

          // Track which cards are being updated
          const updatingIds = new Set();
          highRiskMatches.forEach(newMatch => {
            const oldMatch = tickets.find(m => m.id === newMatch.id);
            if (oldMatch && JSON.stringify(oldMatch) !== JSON.stringify(newMatch)) {
              updatingIds.add(newMatch.id);
            }
          });

          if (updatingIds.size > 0) {
            setUpdatingCards(updatingIds);
            // Clear the updating state after animation
            setTimeout(() => {
              if (isMounted) setUpdatingCards(new Set());
            }, 1000);
          }
        });
      } catch (error) {
        console.error('Error setting up subscription:', error);
      }
    };

    const fetchTickets = async () => {
      if (!isMounted) return;

      setLoading(true);

      try {
        // Load real matches from Firebase
        const matches = await firebaseService.getAllMatches();
        console.log('HighRisk - getAllMatches returned:', matches);
        if (!isMounted) return;

        // Filter only high-risk and forecast category matches (all dates)
        let highRiskMatches = matches.filter(match =>
          match.category === 'high-risk' || match.category === 'forecast'
        );

        console.log('HighRisk - Setting tickets (high-risk category only):', highRiskMatches);
        setTickets(highRiskMatches);
      } catch (error) {
        console.error('Error loading high-risk matches:', error);
        if (!isMounted) return;
        setTickets([]);
      }

      if (isMounted) setLoading(false);
    };

    fetchTickets();
    setupSubscription();

    // Cleanup function
    return () => {
      isMounted = false;
      if (unsubscribe && typeof unsubscribe === 'function') {
        try {
          unsubscribe();
        } catch (error) {
          console.error('Error unsubscribing:', error);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Empty dependency array - only run once on mount

  if (loading) {
    return (
      <div className="high-risk">
        <div className="container">
          <div className="loading-container">
            <div className="spinner"></div>
            <p className="loading-text">იტვირთება მაღალი რისკის პროგნოზები...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <motion.div
      className="high-risk"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{ duration: 0.5 }}
    >
      <div className="container">

        {/* Tickets */}
        <div className="tickets-section">
          {tickets.length > 0 ? (
            <>
              {Object.entries(groupTicketsByDate(tickets))
                .sort(([dateA], [dateB]) => new Date(dateB) - new Date(dateA))
                .map(([date, dateTickets], groupIndex) => (
                  <motion.div
                    key={date}
                    className="date-group"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.5 + groupIndex * 0.1 }}
                  >
                    <div className="date-header">
                      <h2 className="date-title">{formatDateHeader(date)}</h2>
                      <span className="date-count">{dateTickets.length} მატჩი</span>
                    </div>
                    <div className="tickets-list">
                      <div className="tc-header">
                        <span className="tc-header-label">კლუბები</span>
                        <span className="tc-header-label">პროგნოზი</span>
                        <span className="tc-header-label">კოეფ. | თარიღი</span>
                      </div>
                      {dateTickets.map((ticket, index) => (
                        <TicketCard
                          key={ticket.id}
                          ticket={ticket}
                          index={index}
                          isUpdating={updatingCards.has(ticket.id)}
                        />
                      ))}
                    </div>
                  </motion.div>
                ))}
            </>
          ) : (
            <div className="empty-container">
              <div className="empty-icon">🎯</div>
              <h3 className="empty-title">წინასწარი პროგნოზი არ არის</h3>
              <p className="empty-message">
                წინასწარი პროგნოზი არ არის ....
              </p>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );
};

export default HighRisk;
